import { AutomateTask, success } from "@iflyrpa/share";
import type { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { Xhshow } from "../../utils/xhsXsEncrypt";
import type { FetchArticlesParams } from ".";
import { errorResponse, type FetchArticlesResponse, type XiaohongshuArticle } from "./types";

interface Note {
    id: string;
    sticky: boolean;
    schedule_post_time: number;
    comments_count: number;
    tab_status: number;
    display_title: string;
    time: string;
    view_count: number;
    shared_count: number;
    images_list: {
        url: string;
    }[];
    permission_code: number;
    permission_msg: string;
    level: number;
    xsec_token: string;
    xsec_source: string;
    type: string;
    likes: number;
    collected_count: number;
    goods_info: {
        goods_name: string[];
    };
}
type ArticleApiResponse = {
    data: {
        notes: Note[];
        page: number;
        tags: {
            notes_count: number;
        }[];
    };
};
export async function handleXiaohongshuData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    const {
        cookies,
        pageNum = 1,
        pageSize = 10,
        showOriginalData = false,
        onlySuccess = false,
        containsArticle = false,
    } = params;

    const xsEncrypt = new Xhshow();
    const a1Cookie = cookies.find((it) => it.name === "a1")?.value;
    if (!a1Cookie) return { code: 414, message: "小红书数据获取失败，请检查账号状态", data: {} };
    if (onlySuccess && pageSize !== 10) return { code: 414, message: "小红书pageSize不可修改", data: {} };
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        referer: "https://creator.xiaohongshu.com",
        origin: "https://creator.xiaohongshu.com",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];

    const http = new Http(...args);
    const recordCookie = params.cookies.reduce<Record<string, string>>((acc, cookie) => {
        if (cookie.name && cookie.value) {
            acc[cookie.name] = cookie.value;
        }
        return acc;
    }, {});

    async function fetchArticles(pageNum: number, onlySuccess = false): Promise<ArticleApiResponse> {
        const serveUrl = "/web_api/sns/v5/creator/note/user/posted";
        const serveParams = {
            tab: onlySuccess ? 1 : 0,
            page: pageNum - 1,
        };
        const serveXsHeader = xsEncrypt.signHeadersGet(serveUrl, recordCookie, "xhs-pc-web", serveParams);

        return await http.api<ArticleApiResponse>(
            {
                method: "get",
                baseURL: "https://edith.xiaohongshu.com",
                url: serveUrl,
                headers: serveXsHeader,
                params: serveParams,
            },
            { retries: 3, retryDelay: 20, timeout: 3000 },
        );
    }
    // schedule_post_time === 0 means the note is already published;
    // a non-zero value means it is still waiting for its scheduled publish time.
    const isPublished = (note: Note) => !note.schedule_post_time;

    const articleInfo = await fetchArticles(pageNum, onlySuccess);

    let hasNextpage = articleInfo.data.page !== -1;
    const filtered = articleInfo.data.notes.filter(isPublished);
    let currentPage = pageNum + 1;
    const usedPages = new Set<number>();
    while (filtered.length < pageSize && hasNextpage) {
        if (usedPages.has(currentPage)) {
            currentPage++;
            continue;
        }

        const res = await fetchArticles(currentPage, onlySuccess);
        usedPages.add(currentPage);

        const validItems = res.data.notes.filter(isPublished);
        filtered.push(...validItems);

        currentPage++;
        hasNextpage = res.data.page !== -1;
    }

    const getDetailContent = async (id: string) => {
        try {
            const detailUrl = "/web_api/sns/capa/postgw/note/detail";
            const detailParams = {
                note_id: id,
                source: "official",
                edit_mode: 1,
            };
            const detailxsHeader = xsEncrypt.signHeadersGet(detailUrl, recordCookie, "xhs-pc-web", detailParams);

            const detailInfo = await http.api<{ data: { desc: string } }>(
                {
                    method: "get",
                    baseURL: "https://edith.xiaohongshu.com",
                    url: detailUrl,
                    headers: detailxsHeader,
                    params: detailParams,
                },
                { retries: 3, retryDelay: 20, timeout: 3000 },
            );
            return detailInfo.data.desc;
        } catch (err) {
            console.error("❌ JSON 解析失败:", err);
            return null;
        }
    };

    const final = filtered.slice(0, pageSize);
    const articleCell: XiaohongshuArticle[] = await Promise.all(
        final.map(async (item) => ({
            title: item.display_title,
            imageUrl: item.images_list[0].url,
            createTime: Math.floor(new Date(item.time.replace(/-/g, "/")).getTime() / 1000),
            redirectUrl: `https://www.xiaohongshu.com/explore/${item.id}?xsec_token=${item.xsec_token}&xsec_source=pc_creatormng`,
            readNum: item.view_count,
            likeNum: item.likes,
            commentNum: item.comments_count,
            collectNum: item.collected_count,
            shareNum: item.shared_count,
            // articleStates: item.status,
            ...(showOriginalData ? { originalData: item } : {}),
            ...(containsArticle ? { article: await getDetailContent(item.id) } : {}),
        })),
    );

    return success(
        {
            articleCell,
            ...(onlySuccess
                ? {
                      pagination: {
                          nextPage: hasNextpage,
                          pageNum,
                          pageSize,
                      },
                  }
                : null),
        },
        "小红书文章数据获取成功",
    );
}
